/**
 * Invoice theme presets. A theme is a named bundle of the individual invoice
 * settings — colors, font, and layout — so picking one loads those values into
 * the settings form (which stays the single source of truth). The server
 * template renders whatever colors/font/template land in the settings; it does
 * not need to know these presets exist.
 *
 * `font_family` keys must match the server-side stacks in
 * App\Services\Invoice\InvoiceSettingsService::fontStacks().
 */
export interface InvoiceThemePreset {
    key: string;
    label: string;
    description: string;
    template_id: 'classic' | 'modern' | 'friendly';
    font_family: 'sans' | 'humanist' | 'serif';
    primary_color: string;
    secondary_color: string;
    text_color: string;
}

export const INVOICE_THEMES: InvoiceThemePreset[] = [
    {
        key: 'classic',
        label: 'Classic',
        description: 'Clean and professional — the default look.',
        template_id: 'classic',
        font_family: 'sans',
        primary_color: '#1C2A30',
        secondary_color: '#DDE1E3',
        text_color: '#1C2A30',
    },
    {
        key: 'bold',
        label: 'Bold',
        description: 'High-contrast with a colored banner.',
        template_id: 'modern',
        font_family: 'humanist',
        primary_color: '#0A0A0A',
        secondary_color: '#D0D0D0',
        text_color: '#1A1A1A',
    },
    {
        key: 'elegant',
        label: 'Elegant',
        description: 'Warm palette with a refined serif.',
        template_id: 'classic',
        font_family: 'serif',
        primary_color: '#2B2620',
        secondary_color: '#DED7C9',
        text_color: '#2B2620',
    },
    {
        key: 'minimal',
        label: 'Minimal',
        description: 'Airy and monochrome, thin weights.',
        template_id: 'classic',
        font_family: 'sans',
        primary_color: '#3A3A3A',
        secondary_color: '#ECECEC',
        text_color: '#222222',
    },
    {
        key: 'friendly',
        label: 'Friendly',
        description: 'Warm and approachable — accents, badge, thank-you.',
        template_id: 'friendly',
        font_family: 'humanist',
        primary_color: '#14808C',
        secondary_color: '#DDE7E8',
        text_color: '#22333B',
    },
];

export function findInvoiceTheme(key: string | undefined): InvoiceThemePreset | undefined {
    return INVOICE_THEMES.find((theme) => theme.key === key);
}

export function findMatchingInvoiceTheme(settings: {
    template_id?: string;
    font_family?: string;
    primary_color?: string;
    secondary_color?: string;
    text_color?: string;
}): InvoiceThemePreset | undefined {
    return INVOICE_THEMES.find((theme) => (
        theme.template_id === settings.template_id
        && theme.font_family === settings.font_family
        && theme.primary_color.toLowerCase() === settings.primary_color?.toLowerCase()
        && theme.secondary_color.toLowerCase() === settings.secondary_color?.toLowerCase()
        && theme.text_color.toLowerCase() === settings.text_color?.toLowerCase()
    ));
}
